import React, { type JSX, type MouseEvent } from 'react';
import classNames from 'classnames';

import { H3, Link, P, useLocalize } from '../../../utilities';

import illustrations from './illustrations';

import './ShortcutTile.css';

interface ShortcutTileProps {
  shortcut: {
    id: string;
    link?: string;
  };
  className?: string;
  onCustomClick: (id: string) => void;
  isSmallTabletFormFactor?: boolean;
}

/**
 * View component for a single shortcut tile that provides navigation.
 */
export default function ShortcutTile({
  shortcut: { id, link },
  onCustomClick,
  isSmallTabletFormFactor = false,
  ...props
}: ShortcutTileProps): JSX.Element {
  const localize = useLocalize();
  const isCustomLink = id.startsWith('custom:');
  const className = classNames(
    'Nav_ShortcutTile',
    props.className,
    isSmallTabletFormFactor && 'Nav_ShortcutTile--smallTablet'
  );
  const messageKey =
    id === 'custom:start-access-request'
      ? 'NewRequestHome.QuickLink.AccessRequest.Label'
      : `gen2.leftnav.label.${id}`;
  const text = localize(messageKey);
  const Illustration = illustrations[id];

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onCustomClick(getCustomId(id));
  };

  const Element = isSmallTabletFormFactor ? H3 : P;
  const content = (
    <div className='Nav_ShortcutTile-content'>
      <div className='Nav_ShortcutTile-content-icon'>
        <Illustration />
      </div>
      <div className='Nav_ShortcutTile-content-label'>
        <Element className='Nav_ShortcutTile-content-label-text'>
          {text}
        </Element>
      </div>
    </div>
  );

  if (isCustomLink) {
    return (
      <button className={className} onClick={handleClick} type='button'>
        {content}
      </button>
    );
  }

  if (!link) {
    throw new Error(`Shortcut ${id} has no link`);
  }

  return (
    <Link className={className} href={link}>
      {content}
    </Link>
  );
}

function getCustomId(id: string): string {
  return id.replace('custom:', '');
}
